import React, { useEffect, useState, useCallback } from 'react';
import {
  BookOpen, MapPin, DoorOpen, Euro, Plus, Trash2,
  Save, Loader2, Search, Settings2, CalendarDays, RefreshCw,
} from 'lucide-react';
import api from '@/lib/axios';
import { matchesFreeText } from '@/components/search/SearchBuilder';
import { toast } from 'sonner';
import { formatDeNumber } from '@/lib/utils';
import Layout from '@/components/layout/Layout';
import { useActionBarConfig } from '@/contexts/ActionBarContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PlzOrtBridge } from '@/components/address/PlzOrtBridge';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useListKeyboardNav, { LIST_ROW_CLASS } from '@/hooks/useListKeyboardNav';
import GkzGroupsTab from './GkzGroupsTab';

// Reusable CRUD Table Component
function CrudTable({ 
  title, 
  items, 
  columns, 
  onAdd, 
  onEdit, 
  onDelete, 
  loading,
  searchPlaceholder = "Suchen...",
  addButtonText = "Neu"
}) {
  const [search, setSearch] = useState('');
  
  const filteredItems = items.filter((item) => matchesFreeText(item, search));

  const { getRowProps } = useListKeyboardNav({
    items: filteredItems,
    onSelect: onEdit,
  });

  return (
    <div className="bg-white rounded-xl border border-slate-200">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={onAdd} size="sm" className="bg-slate-900 text-white hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" />
          {addButtonText}
        </Button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 text-slate-500 font-medium text-xs uppercase tracking-wider border-b border-slate-200">
            <tr>
              {columns.map(col => (
                <th key={col.key} className="p-3 text-left">{col.label}</th>
              ))}
              <th className="p-3 text-right w-12" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="p-8 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                </td>
              </tr>
            ) : filteredItems.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="p-8 text-center text-slate-500">
                  Keine Einträge gefunden
                </td>
              </tr>
            ) : (
              filteredItems.map((item, idx) => (
                <tr key={item.id} className={LIST_ROW_CLASS} {...getRowProps(idx, item)}>
                  {columns.map(col => (
                    <td key={col.key} className="p-3 text-sm text-slate-700">
                      {col.render ? col.render(item[col.key], item) : item[col.key] || '-'}
                    </td>
                  ))}
                  <td className="p-3 text-right">
                    <Button variant="ghost" size="icon" onClick={() => onDelete(item)} className="h-8 w-8" title="Löschen">
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      <div className="p-3 border-t border-slate-200 text-xs text-slate-500">
        {filteredItems.length} von {items.length} Einträgen
      </div>
    </div>
  );
}

export default function MasterDataPage() {
  const [activeTab, setActiveTab] = useState('subjects');

  const NEW_BY_TAB = {
    subjects: ['subject', 'Neues Fach anlegen'],
    locations: ['location', 'Neuen Standort anlegen'],
    rooms: ['room', 'Neuen Raum anlegen'],
    tariffs: ['tariff', 'Neuen Tarif anlegen'],
  };
  useActionBarConfig({
    onNew: NEW_BY_TAB[activeTab] ? () => openAddDialog(NEW_BY_TAB[activeTab][0]) : null,
    newLabel: NEW_BY_TAB[activeTab]?.[1],
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  
  // Data states
  const [subjects, setSubjects] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [holidays, setHolidays] = useState(null);
  const [holidayYear, setHolidayYear] = useState(new Date().getFullYear());
  
  // Loading states
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [loadingTariffs, setLoadingTariffs] = useState(true);
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState(null); // 'subject', 'location', 'room', 'tariff'
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({});

  // Load all data
  const loadSubjects = useCallback(async () => {
    try {
      setLoadingSubjects(true);
      const response = await api.get('/subjects');
      setSubjects(response.data);
    } catch (error) {
      console.error('Error loading subjects:', error);
    } finally {
      setLoadingSubjects(false);
    }
  }, []);

  const loadLocations = useCallback(async () => {
    try {
      setLoadingLocations(true);
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  const loadRooms = useCallback(async () => {
    try {
      setLoadingRooms(true);
      const response = await api.get('/locations/rooms');
      setRooms(response.data);
    } catch (error) {
      console.error('Error loading rooms:', error);
    } finally {
      setLoadingRooms(false);
    }
  }, []);

  const loadTariffs = useCallback(async () => {
    try {
      setLoadingTariffs(true);
      const response = await api.get('/tariffs-discounts/tariffs');
      setTariffs(response.data);
    } catch (error) {
      console.error('Error loading tariffs:', error);
    } finally {
      setLoadingTariffs(false);
    }
  }, []);

  const loadHolidays = useCallback(async (year) => {
    try {
      setLoadingHolidays(true);
      const response = await api.get(`/holidays/combined/calendar?year=${year}`);
      setHolidays(response.data);
    } catch (error) {
      console.error('Error loading holidays:', error);
      toast.error('Fehler beim Laden der Feiertage');
    } finally {
      setLoadingHolidays(false);
    }
  }, []);

  useEffect(() => {
    loadSubjects();
    loadLocations();
    loadRooms();
    loadTariffs();
  }, [loadSubjects, loadLocations, loadRooms, loadTariffs]);

  // Load holidays when tab changes or year changes
  useEffect(() => {
    if (activeTab === 'holidays') {
      loadHolidays(holidayYear);
    }
  }, [activeTab, holidayYear, loadHolidays]);

  // Dialog handlers
  const openAddDialog = (type) => {
    setDialogType(type);
    setDialogMode('add');
    setEditingItem(null);
    setFormData(getDefaultFormData(type));
    setShowDialog(true);
  };

  const openEditDialog = (type, item) => {
    setDialogType(type);
    setDialogMode('edit');
    setEditingItem(item);
    setFormData({ ...item });
    setShowDialog(true);
  };

  const getDefaultFormData = (type) => {
    switch (type) {
      case 'subject':
        return { name: '', category: '', description: '' };
      case 'location':
        return { name: '', address: '', city: '', postal_code: '', phone: '', email: '', is_active: true };
      case 'room':
        return { name: '', location_id: '', capacity: 1, equipment: '', is_active: true };
      case 'tariff':
        return { name: '', amount: 0, billing_period: 'monthly', description: '', is_active: true };
      default:
        return {};
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let endpoint = '';
      let data = { ...formData };
      
      switch (dialogType) {
        case 'subject':
          endpoint = '/subjects';
          if (dialogMode === 'edit') {
            await api.put(`${endpoint}/${editingItem.id}`, data);
          } else {
            await api.post(endpoint, data);
          }
          loadSubjects();
          break;
          
        case 'location':
          endpoint = '/locations';
          if (dialogMode === 'edit') {
            await api.put(`${endpoint}/${editingItem.id}`, data);
          } else {
            await api.post(endpoint, data);
          }
          loadLocations();
          break;
          
        case 'room':
          endpoint = '/locations/rooms';
          if (dialogMode === 'edit') {
            await api.put(`${endpoint}/${editingItem.id}`, data);
          } else {
            await api.post(endpoint, data);
          }
          loadRooms();
          break;
          
        case 'tariff':
          endpoint = '/tariffs-discounts/tariffs';
          data.amount = parseFloat(data.amount) || 0;
          if (dialogMode === 'edit') {
            await api.put(`${endpoint}/${editingItem.id}`, data);
          } else {
            await api.post(endpoint, data);
          }
          loadTariffs();
          break;
      }
      
      toast.success(dialogMode === 'add' ? 'Erfolgreich erstellt' : 'Erfolgreich aktualisiert');
      setShowDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, item) => {
    if (!confirm(`Möchten Sie "${item.name}" wirklich löschen?`)) return;
    
    try {
      let endpoint = '';
      switch (type) {
        case 'subject':
          endpoint = `/subjects/${item.id}`;
          await api.delete(endpoint);
          loadSubjects();
          break;
        case 'location':
          endpoint = `/locations/${item.id}`;
          await api.delete(endpoint);
          loadLocations();
          break;
        case 'room':
          endpoint = `/locations/rooms/${item.id}`;
          await api.delete(endpoint);
          loadRooms();
          break;
        case 'tariff':
          endpoint = `/tariffs-discounts/tariffs/${item.id}`;
          await api.delete(endpoint);
          loadTariffs();
          break;
      }
      toast.success('Erfolgreich gelöscht');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // Column definitions
  const subjectColumns = [
    { key: 'name', label: 'Name' },
    { key: 'category', label: 'Kategorie' },
    { key: 'description', label: 'Beschreibung' },
  ];

  const locationColumns = [
    { key: 'name', label: 'Name' },
    { key: 'address', label: 'Adresse' },
    { key: 'city', label: 'Stadt' },
    { key: 'phone', label: 'Telefon' },
    { 
      key: 'is_active', 
      label: 'Status',
      render: (val) => (
        <Badge className={val ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
          {val ? 'Aktiv' : 'Inaktiv'}
        </Badge>
      )
    },
    { key: 'room_count', label: 'Räume' },
  ];

  const roomColumns = [
    { key: 'name', label: 'Name' },
    { key: 'location_name', label: 'Standort' },
    { key: 'capacity', label: 'Kapazität' },
    { key: 'equipment', label: 'Ausstattung' },
    { 
      key: 'is_active', 
      label: 'Status',
      render: (val) => (
        <Badge className={val ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
          {val ? 'Aktiv' : 'Inaktiv'}
        </Badge>
      )
    },
  ];

  const tariffColumns = [
    { key: 'name', label: 'Name' },
    { 
      key: 'amount', 
      label: 'Betrag',
      render: (val) => `${formatDeNumber(val || 0)} €`
    },
    { key: 'billing_period', label: 'Abrechnungszeitraum' },
    { 
      key: 'is_active', 
      label: 'Status',
      render: (val) => (
        <Badge className={val ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
          {val ? 'Aktiv' : 'Inaktiv'}
        </Badge>
      )
    },
  ];

  // Render dialog form
  const renderDialogForm = () => {
    switch (dialogType) {
      case 'subject':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Name *</label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Klavier"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Kategorie</label>
              <Select value={formData.category || ''} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Kategorie wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Tasteninstrumente">Tasteninstrumente</SelectItem>
                  <SelectItem value="Streichinstrumente">Streichinstrumente</SelectItem>
                  <SelectItem value="Zupfinstrumente">Zupfinstrumente</SelectItem>
                  <SelectItem value="Blasinstrumente">Blasinstrumente</SelectItem>
                  <SelectItem value="Schlaginstrumente">Schlaginstrumente</SelectItem>
                  <SelectItem value="Gesang">Gesang</SelectItem>
                  <SelectItem value="Theorie">Theorie</SelectItem>
                  <SelectItem value="Ensemble">Ensemble</SelectItem>
                  <SelectItem value="Sonstiges">Sonstiges</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Beschreibung</label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optionale Beschreibung"
              />
            </div>
          </div>
        );
        
      case 'location':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700">Name *</label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Hauptgebäude"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700">Adresse</label>
                <Input
                  value={formData.address || ''}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Straße und Hausnummer"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">PLZ</label>
                <Input
                  value={formData.postal_code || ''}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  placeholder="12345"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Stadt</label>
                <Input
                  value={formData.city || ''}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Musterstadt"
                />
                <PlzOrtBridge
                  postalCode={formData.postal_code}
                  city={formData.city}
                  onCity={(value) => setFormData((prev) => ({ ...prev, city: value }))}
                  testId="masterdata-location-plz"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Telefon</label>
                <Input
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+49 123 456789"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">E-Mail</label>
                <Input
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="standort@musikschule.de"
                />
              </div>
            </div>
          </div>
        );
        
      case 'room':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Name *</label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Raum 101"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Standort *</label>
              <Select value={formData.location_id || ''} onValueChange={(v) => setFormData({ ...formData, location_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Standort wählen" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Kapazität</label>
              <Input
                type="number"
                value={formData.capacity || 1}
                onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
                min={1}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Ausstattung</label>
              <Input
                value={formData.equipment || ''}
                onChange={(e) => setFormData({ ...formData, equipment: e.target.value })}
                placeholder="z.B. Klavier, Whiteboard"
              />
            </div>
          </div>
        );
        
      case 'tariff':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Name *</label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Einzelunterricht 45 Min."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Betrag (€) *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount || 0}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  min={0}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Abrechnungszeitraum</label>
                <Select value={formData.billing_period || 'monthly'} onValueChange={(v) => setFormData({ ...formData, billing_period: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monatlich</SelectItem>
                    <SelectItem value="quarterly">Vierteljährlich</SelectItem>
                    <SelectItem value="yearly">Jährlich</SelectItem>
                    <SelectItem value="per_lesson">Pro Unterricht</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Beschreibung</label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optionale Beschreibung"
              />
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  const getDialogTitle = () => {
    const titles = {
      subject: dialogMode === 'add' ? 'Neues Fach' : 'Fach bearbeiten',
      location: dialogMode === 'add' ? 'Neuer Standort' : 'Standort bearbeiten',
      room: dialogMode === 'add' ? 'Neuer Raum' : 'Raum bearbeiten',
      tariff: dialogMode === 'add' ? 'Neuer Tarif' : 'Tarif bearbeiten',
    };
    return titles[dialogType] || '';
  };

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Settings2 className="h-8 w-8 text-slate-700" />
            <h1 className="text-4xl font-bold tracking-tight text-slate-900">
              Stammdatenverwaltung
            </h1>
          </div>
          <p className="text-slate-600">Grundlegende Parameter für die Musikschulverwaltung</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="subjects" className="data-[state=active]:bg-white">
              <BookOpen className="h-4 w-4 mr-2" />
              Fächer ({subjects.length})
            </TabsTrigger>
            <TabsTrigger value="locations" className="data-[state=active]:bg-white">
              <MapPin className="h-4 w-4 mr-2" />
              Standorte ({locations.length})
            </TabsTrigger>
            <TabsTrigger value="rooms" className="data-[state=active]:bg-white">
              <DoorOpen className="h-4 w-4 mr-2" />
              Räume ({rooms.length})
            </TabsTrigger>
            <TabsTrigger value="tariffs" className="data-[state=active]:bg-white">
              <Euro className="h-4 w-4 mr-2" />
              Tarife ({tariffs.length})
            </TabsTrigger>
            <TabsTrigger value="holidays" className="data-[state=active]:bg-white">
              <CalendarDays className="h-4 w-4 mr-2" />
              Feiertage & Ferien
            </TabsTrigger>
            <TabsTrigger value="gkz" className="data-[state=active]:bg-white" data-testid="stammdaten-tab-gkz">
              <MapPin className="h-4 w-4 mr-2" />
              GKZ-Gruppen
            </TabsTrigger>
          </TabsList>

          <TabsContent value="subjects">
            <CrudTable
              title="Fächer"
              items={subjects}
              columns={subjectColumns}
              onAdd={() => openAddDialog('subject')}
              onEdit={(item) => openEditDialog('subject', item)}
              onDelete={(item) => handleDelete('subject', item)}
              loading={loadingSubjects}
              searchPlaceholder="Fächer suchen..."
              addButtonText="Neues Fach"
            />
          </TabsContent>

          <TabsContent value="locations">
            <CrudTable
              title="Standorte"
              items={locations}
              columns={locationColumns}
              onAdd={() => openAddDialog('location')}
              onEdit={(item) => openEditDialog('location', item)}
              onDelete={(item) => handleDelete('location', item)}
              loading={loadingLocations}
              searchPlaceholder="Standorte suchen..."
              addButtonText="Neuer Standort"
            />
          </TabsContent>

          <TabsContent value="rooms">
            <CrudTable
              title="Räume"
              items={rooms}
              columns={roomColumns}
              onAdd={() => openAddDialog('room')}
              onEdit={(item) => openEditDialog('room', item)}
              onDelete={(item) => handleDelete('room', item)}
              loading={loadingRooms}
              searchPlaceholder="Räume suchen..."
              addButtonText="Neuer Raum"
            />
          </TabsContent>

          <TabsContent value="tariffs">
            <CrudTable
              title="Tarife"
              items={tariffs}
              columns={tariffColumns}
              onAdd={() => openAddDialog('tariff')}
              onEdit={(item) => openEditDialog('tariff', item)}
              onDelete={(item) => handleDelete('tariff', item)}
              loading={loadingTariffs}
              searchPlaceholder="Tarife suchen..."
              addButtonText="Neuer Tarif"
            />
          </TabsContent>

          <TabsContent value="holidays">
            <div className="bg-white rounded-xl border border-slate-200">
              {/* Header with year selection */}
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Feiertage & Schulferien</h2>
                  <p className="text-sm text-slate-600">Automatisch geladen für NRW und Niedersachsen</p>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={holidayYear.toString()} onValueChange={(v) => setHolidayYear(parseInt(v))}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027, 2028].map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => loadHolidays(holidayYear)}
                    disabled={loadingHolidays}
                  >
                    {loadingHolidays ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Legend */}
              {holidays?.legend && (
                <div className="p-4 border-b border-slate-200 bg-slate-50">
                  <div className="flex flex-wrap gap-6">
                    {holidays.legend.map(l => (
                      <div key={l.state} className="flex items-center gap-4">
                        <span className="font-medium text-sm text-slate-700">{l.name}:</span>
                        <div className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded" style={{ backgroundColor: l.feiertag_color }}></span>
                          <span className="text-xs text-slate-600">Feiertage</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded" style={{ backgroundColor: l.ferien_color }}></span>
                          <span className="text-xs text-slate-600">Ferien</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Content */}
              <div className="p-4">
                {loadingHolidays ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                    <span className="ml-3 text-slate-600">Lade Feiertage und Ferien...</span>
                  </div>
                ) : holidays?.events ? (
                  <div className="space-y-6">
                    {/* Group by month */}
                    {Object.entries(
                      holidays.events.reduce((acc, event) => {
                        const month = new Date(event.start_date).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
                        if (!acc[month]) acc[month] = [];
                        acc[month].push(event);
                        return acc;
                      }, {})
                    ).map(([month, events]) => (
                      <div key={month}>
                        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">{month}</h3>
                        <div className="space-y-2">
                          {events.map((event, idx) => (
                            <div 
                              key={`${event.name}-${event.start_date}-${idx}`}
                              className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50"
                            >
                              <div 
                                className="w-1 h-12 rounded-full flex-shrink-0"
                                style={{ backgroundColor: event.color }}
                              ></div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-900">{event.name}</span>
                                  <Badge 
                                    className="text-xs"
                                    style={{ 
                                      backgroundColor: event.color + '20',
                                      color: event.color,
                                      borderColor: event.color
                                    }}
                                  >
                                    {event.state_name}
                                  </Badge>
                                </div>
                                <div className="text-sm text-slate-600">
                                  {event.type === 'ferien' ? (
                                    <>
                                      {new Date(event.start_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                      {' - '}
                                      {new Date(event.end_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    </>
                                  ) : (
                                    new Date(event.start_date).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })
                                  )}
                                </div>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {event.type === 'feiertag' ? 'Feiertag' : 'Ferien'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    Keine Daten verfügbar
                  </div>
                )}
              </div>
              
              {/* Footer */}
              {holidays?.events && (
                <div className="p-3 border-t border-slate-200 text-xs text-slate-500">
                  {holidays.events.filter(e => e.type === 'feiertag').length} Feiertage, {holidays.events.filter(e => e.type === 'ferien').length} Ferienzeiten für {holidayYear}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="gkz">
            <GkzGroupsTab />
          </TabsContent>
        </Tabs>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{getDialogTitle()}</DialogTitle>
              <DialogDescription>
                {dialogMode === 'add' ? 'Neuen Eintrag erstellen' : 'Bestehenden Eintrag bearbeiten'}
              </DialogDescription>
            </DialogHeader>
            
            {renderDialogForm()}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSave} disabled={saving || !formData.name}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
